import type { Site } from '../types';

/**
 * Escape a value for CSV (handle commas, quotes, and injection)
 */
function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);

  // Prevent CSV injection
  if (/^[=+\-@\t\r]/.test(str)) {
    str = "'" + str;
  }

  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Get account name from site (V4 has accountName directly)
 */
function getAccountName(site: Site): string {
  // V4: use accountName from JOIN
  if (site.accountName) return site.accountName;
  return '';
}

/**
 * Format full address from site fields (V4 field names with V3 fallbacks)
 */
function formatAddress(site: Site): string {
  return [
    site.addressLine1 || site.address,
    site.city,
    site.stateProvince || site.state_province_county,
    site.postalCode || site.postcode,
    site.country,
  ]
    .filter(Boolean)
    .join(', ');
}

const COLUMNS: Array<{ key: string; label: string; getValue: (site: Site) => string }> = [
  { key: 'name', label: 'Name', getValue: (s) => s.name || '' },
  { key: 'account', label: 'Account', getValue: (s) => getAccountName(s) },
  { key: 'accountNumber', label: 'Account Number', getValue: (s) => s.accountNumber || '' },
  { key: 'abbreviations', label: 'Abbreviations', getValue: (s) => s.abbreviations?.join('; ') || '' },
  { key: 'tags', label: 'Tags', getValue: (s) => s.tags?.join('; ') || '' },
  { key: 'address', label: 'Address', getValue: (s) => formatAddress(s) },
  {
    key: 'phone',
    label: 'Phone',
    getValue: (s) => {
      const ext = s.phoneExtension || s.extension;
      return (s.phone || '') + (ext ? ` x${ext}` : '');
    },
  },
  { key: 'email', label: 'Email', getValue: (s) => s.email || '' },
  { key: 'status', label: 'Status', getValue: (s) => (s.disabled ? 'Inactive' : 'Active') },
  {
    key: 'createdAt',
    label: 'Created At',
    getValue: (s) => {
      const date = s.createdAt || s.created_at;
      return date ? new Date(date).toLocaleDateString() : '';
    },
  },
];

/**
 * Export sites to CSV and trigger download
 */
export function exportSitesToCSV(sites: Site[], filename = 'sites'): void {
  const header = COLUMNS.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = sites.map((site) =>
    COLUMNS.map((c) => escapeCSVValue(c.getValue(site))).join(',')
  );

  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
